/* eslint-disable max-len */
import { AxiosError } from 'axios';
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  isSameDay,
  isSameMonth,
  isToday,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import Btn from '../../../../../../components/Buttons/Btn';
import Modal from '../../../../../../components/Modal';
import { backendErrorNotification } from '../../../../../../components/Notification';
import SelectAsyncJSX from '../../../../../../components/SelectAsync';
import HttpStatus from '../../../../../../enums/httpStatus';
import {
  visitScheduledAPI,
  sellersApi,
} from '../../../../../../services/requests';
import { ApiResPaginated } from '../../../../../../types/apiResponseTypes';
import { ScheduledVisit, SellerVisitRoutineSummary } from '../../../../../../types';
import { getOptionsFromSellers } from '../../../../../../utils/getSelectOptions';
import ModalVisitScheduleDetail from '../ModalVisitScheduleDetail';

type Props = {
  closeModal: () => void;
  selectedItem: SellerVisitRoutineSummary | null;
  callback: () => void;
};

type Option = {
  value: any;
  label: string;
};

const parseScheduledDate = (value?: string | null): Date => {
  if (!value) {
    return new Date();
  }

  return new Date(value.replace(' ', 'T'));
};

const getCustomerLabel = (visit: ScheduledVisit) => {
  const code = visit.customer?.CodigoCliente ?? visit.CodigoCliente;
  const name = visit.customer?.RazaoSocial ?? '-';

  return `${code} - ${name}`;
};

const getCalendarLocale = (language?: string) => {
  const languageCode = language?.split('-')[0] ?? 'pt';

  switch (languageCode) {
    case 'en':
      return 'en-US';
    case 'pt':
    default:
      return 'pt-BR';
  }
};

const formatMonthLabel = (value: Date, locale: string) =>
  capitalizeFirstLetter(
    new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(value)
  );

const formatWeekDayLabel = (value: Date, locale: string) =>
  new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(value);

const formatTimeLabel = (value: string | null | undefined, locale: string) =>
  new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(parseScheduledDate(value));

const capitalizeFirstLetter = (value: string) =>
  value.length > 0 ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const getDefaultStandaloneVisitDate = (selectedDate?: Date): Date => {
  const now = new Date();
  const baseDate = selectedDate ? new Date(selectedDate) : new Date();

  if (isSameDay(baseDate, now)) {
    const todayWithCurrentTimePlusThreeHours = new Date(now);
    todayWithCurrentTimePlusThreeHours.setHours(
      todayWithCurrentTimePlusThreeHours.getHours() + 3
    );

    return todayWithCurrentTimePlusThreeHours;
  }

  baseDate.setHours(15, 0, 0, 0);

  return baseDate;
};

const isPastCalendarDay = (value: Date): boolean =>
  startOfDay(value).getTime() < startOfDay(new Date()).getTime();

const ModalVisitCalendar = ({ closeModal, selectedItem, callback }: Props) => {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const [waitingRequest, setWaitingRequest] = useState(false);
  const [seller, setSeller] = useState<Option | Record<string, unknown>>({});
  const [scheduledVisits, setScheduledVisits] = useState<ScheduledVisit[]>([]);
  const [displayedMonth, setDisplayedMonth] = useState(startOfMonth(new Date()));
  const [detailDate, setDetailDate] = useState(new Date());
  const [selectedVisit, setSelectedVisit] = useState<ScheduledVisit | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const reqGetScheduledVisits = async () => {
    if (!seller.value) {
      setScheduledVisits([]);
      return;
    }

    setWaitingRequest(true);

    try {
      visitScheduledAPI.query = `?page=1&quantityPerPage=1000&scheduled_for_start=${startOfMonth(displayedMonth)
        .toISOString()
        .slice(0, 10)}&scheduled_for_end=${endOfMonth(displayedMonth)
        .toISOString()
        .slice(0, 10)}&CodigoVendedor=${encodeURIComponent(String(seller.value))}`;

      const res = await visitScheduledAPI.index();

      if (res.status !== HttpStatus.OK) {
        throw res;
      }

      const { data }: ApiResPaginated<ScheduledVisit[]> = res.data;

      setScheduledVisits(data);
    } catch (err) {
      if (!visitScheduledAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    }

    visitScheduledAPI.query = '';
    setWaitingRequest(false);
  };

  useEffect(() => {
    if (selectedItem) {
      setSeller({
        value: selectedItem.CodigoVendedor,
        label: `${selectedItem.CodigoVendedor} - ${selectedItem.Nome}`,
      });
    }
  }, [selectedItem]);

  useEffect(() => {
    reqGetScheduledVisits();
  }, [seller, displayedMonth]);

  const closeModalAction = () => {
    callback();
    closeModal();
  };

  const openCreateModal = (date?: Date) => {
    setSelectedVisit(null);
    setDetailDate(getDefaultStandaloneVisitDate(date));
    setShowDetailModal(true);
  };

  const openEditModal = (visit: ScheduledVisit) => {
    setSelectedVisit(visit);
    setDetailDate(parseScheduledDate(visit.scheduled_for));
    setShowDetailModal(true);
  };

  const calendarDays: Date[] = [];
  let currentDay = startOfWeek(startOfMonth(displayedMonth));
  const finalDay = endOfWeek(endOfMonth(displayedMonth));

  while (currentDay <= finalDay) {
    calendarDays.push(currentDay);
    currentDay = addDays(currentDay, 1);
  }

  const monthVisitsCount = scheduledVisits.length;
  const daysWithVisits = new Set(
    scheduledVisits.map((visit) => parseScheduledDate(visit.scheduled_for).toDateString())
  ).size;
  const calendarLocale = getCalendarLocale(i18n.resolvedLanguage ?? i18n.language);

  return (
    <>
      <Modal
        title={t('sellerAppointments')}
        onlyCloseButton
        showModal
        closeModal={closeModalAction}
        isLoading={waitingRequest}
        style={{ width: 'min(76rem, 96vw)', maxWidth: '96vw' }}
        bodyStyle={{ width: '100%' }}
        maximized
      >
        <div>
          <div className="row">
            <div className="itemFormWithLabel">
              <div className="label">{t('selectSeller') as string}</div>
              <SelectAsyncJSX
                value={seller}
                onChange={(value: any) => setSeller(value)}
                request={sellersApi}
                reqResponseToOption={getOptionsFromSellers}
                disabled={waitingRequest}
              />
            </div>

            <div className="itemFormWithoutLabel" style={{ maxWidth: '15rem' }}>
              <div style={summaryCardStyle(theme.colors.onSurfaceLight)}>
                {t('scheduledVisitsInMonth') as string}:&nbsp;
                <strong>{monthVisitsCount}</strong>
              </div>
            </div>

            <div className="itemFormWithoutLabel" style={{ maxWidth: '15rem' }}>
              <div style={summaryCardStyle(theme.colors.onSurfaceLight)}>
                {t('daysWithAppointments') as string}:&nbsp;
                <strong>{daysWithVisits}</strong>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="itemFormWithoutLabel">
              <h1>{formatMonthLabel(displayedMonth, calendarLocale)}</h1>
            </div>

            <div className="itemFormWithoutLabel">
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'end' }}>
                <Btn
                  text={t('previousMonth')}
                  onClick={() => setDisplayedMonth(subMonths(displayedMonth, 1))}
                  style={{ margin: 0 }}
                />
                <Btn
                  text={t('currentMonth')}
                  onClick={() => setDisplayedMonth(startOfMonth(new Date()))}
                  style={{ margin: 0 }}
                />
                <Btn
                  text={t('nextMonth')}
                  onClick={() => setDisplayedMonth(addMonths(displayedMonth, 1))}
                  style={{ margin: 0 }}
                />
              </div>
            </div>
          </div>

          {seller.value ? (
            <div
              // className="itemFormWithoutLabel"
              style={{
                width: '100%',
                maxWidth: '100%',
                overflowX: 'auto',
                overflowY: 'hidden',
                padding: '1rem',
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                  gap: '0.5rem',
                  minWidth: '58rem'
                }}
              >
                {Array.from({ length: 7 }).map((_, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '0.5rem',
                      borderRadius: '0.75rem',
                      background: theme.colors.onSurfaceLight,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      textAlign: 'center',
                      textTransform: 'uppercase',
                    }}
                  >
                    {formatWeekDayLabel(addDays(startOfWeek(new Date()), index), calendarLocale)}
                  </div>
                ))}

                {calendarDays.map((day) => {
                  const dayVisits = scheduledVisits.filter((visit) =>
                    isSameDay(parseScheduledDate(visit.scheduled_for), day)
                  );
                  const isPastDay = isPastCalendarDay(day);

                  return (
                    <div
                      key={day.toISOString()}
                      style={getDayCellStyle({
                        theme,
                        isCurrentMonth: isSameMonth(day, displayedMonth),
                        isToday: isToday(day),
                        hasItems: dayVisits.length > 0,
                      })}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '0.5rem',
                        }}
                      >
                        <span style={getDayNumberStyle(theme, isToday(day))}>
                          {day.getDate()}
                        </span>
                        {dayVisits.length > 0 ? (
                          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: theme.colors.primary }}>
                            {dayVisits.length}
                          </span>
                        ) : null}
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.35rem',
                          overflow: 'auto',
                          paddingBottom: '2.75rem',
                        }}
                      >
                        {dayVisits.map((visit) => (
                          <button
                            key={visit.id}
                            type="button"
                            onClick={() => openEditModal(visit)}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '0.2rem',
                              width: '100%',
                              padding: '0.45rem 0.55rem',
                              border: 0,
                              borderRadius: '0.75rem',
                              background: `${theme.colors.primary}40`,
                              color: theme.colors.text,
                              textAlign: 'left',
                              cursor: 'pointer',
                            }}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', color: theme.colors.primary }}>
                              <p style={{ fontSize: '1.2rem', fontWeight: 700, margin: 0 }}>
                                {formatTimeLabel(visit.scheduled_for, calendarLocale)}
                              </p>
                              <p style={{ fontSize: '0.8rem', margin: 0 }}>
                                {visit.routine_id && `\u00A0(${t('routine')})`}
                              </p>
                            </span>
                            <strong style={{ fontSize: '0.8rem', lineHeight: 1.2, wordBreak: 'break-word' }}>
                              {getCustomerLabel(visit)}
                            </strong>
                          </button>
                        ))}
                      </div>

                      {isSameMonth(day, displayedMonth) ? (
                        <button
                          type="button"
                          title={t('newScheduledVisit') as string}
                          onClick={() => openCreateModal(day)}
                          disabled={waitingRequest || isPastDay}
                          style={{
                            position: 'absolute',
                            right: '0.75rem',
                            bottom: '0.75rem',
                            width: '2.25rem',
                            height: '2.25rem',
                            border: 0,
                            borderRadius: '999px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: theme.colors.primary,
                            color: theme.colors.onPrimary,
                            fontSize: '1.35rem',
                            lineHeight: 1,
                            fontWeight: 700,
                            cursor: waitingRequest || isPastDay ? 'not-allowed' : 'pointer',
                            boxShadow: waitingRequest || isPastDay
                              ? 'none'
                              : `0 6px 18px ${theme.colors.primary}45`,
                            opacity: waitingRequest || isPastDay ? 0.45 : 1,
                          }}
                        >
                          +
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div style={emptyStateStyle(theme.colors.onBackground)}>
              {t('selectSellerToViewSchedules') as string}
            </div>
          )}

          {seller.value && scheduledVisits.length === 0 && !waitingRequest ? (
            <div style={emptyStateStyle(theme.colors.onBackground)}>
              {t('noScheduledVisitsInMonth') as string}
            </div>
          ) : null}
        </div>
      </Modal>

      {showDetailModal ? (
        <ModalVisitScheduleDetail
          seller={seller}
          visit={selectedVisit}
          initialDate={detailDate}
          closeModal={() => setShowDetailModal(false)}
          onSuccess={async () => {
            await reqGetScheduledVisits();
            callback();
          }}
        />
      ) : null}
    </>
  );
};

export default ModalVisitCalendar;

const summaryCardStyle = (background: string) => ({
  padding: '0.85rem 1rem',
  borderRadius: '1rem',
  background,
  minWidth: '10rem',
});

const emptyStateStyle = (borderColor: string) => ({
  padding: '2rem 1rem',
  border: `1px dashed ${borderColor}35`,
  borderRadius: '1rem',
  textAlign: 'center' as const,
});

const getDayCellStyle = ({
  theme,
  isCurrentMonth,
  isToday,
  hasItems,
}: {
  theme: { colors: Record<string, string>; };
  isCurrentMonth: boolean;
  isToday: boolean;
  hasItems: boolean;
}) => ({
  position: 'relative' as const,
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '0.5rem',
  minHeight: '9.5rem',
  padding: '0.75rem',
  borderRadius: '1rem',
  border: `1px solid ${isToday ? theme.colors.primary : hasItems ? `${theme.colors.primary}55` : `${theme.colors.onBackground}25`}`,
  background: isCurrentMonth ? theme.colors.surface : `${theme.colors.onBackground}08`,
  opacity: isCurrentMonth ? 1 : 0.65,
});

const getDayNumberStyle = (
  theme: { colors: Record<string, string>; },
  isTodayDate: boolean
) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '2rem',
  height: '2rem',
  borderRadius: '999px',
  fontWeight: 700,
  background: isTodayDate ? theme.colors.primary : 'transparent',
  color: isTodayDate ? theme.colors.onPrimary : theme.colors.text,
});
